import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Field, FieldValue, Group, GroupPayload, Row } from "../types";
import { settingsQueryKey, createRow, deleteRow } from "../api";
import { StringField } from "../fields/StringField";
import { IntField } from "../fields/IntField";
import { BoolField } from "../fields/BoolField";

type CollectionPayload = Extract<GroupPayload, { kind: "collection" }>;

interface Props {
  group: Group;
  payload: CollectionPayload;
}

function pickCreateFlow(group: Group): "immediate" | "form-first" {
  if (group.create_flow === "immediate" || group.create_flow === "form-first") {
    return group.create_flow;
  }
  const hasRequiredNoDefault = (group.fields ?? []).some(
    (f) => f.required && f.default === undefined,
  );
  return hasRequiredNoDefault ? "form-first" : "immediate";
}

export function CollectionPanel({ group, payload }: Props) {
  const qc = useQueryClient();
  const [slugDraft, setSlugDraft] = useState("");
  const [draftFields, setDraftFields] = useState<Record<string, unknown>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const flow = useMemo(() => pickCreateFlow(group), [group]);

  function validateRequired(): boolean {
    const errs: Record<string, string> = {};
    for (const f of group.fields ?? []) {
      if (!f.required) continue;
      const v = draftFields[f.key];
      if (v === undefined || v === null || v === "") errs[f.key] = "required";
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function resetDraft() {
    setDraftFields({});
    setFieldErrors({});
    setSlugDraft("");
    setError(null);
    setShowNewForm(false);
  }

  const create = useMutation({
    mutationFn: (body: { id?: string; fields: Record<string, unknown> }) =>
      createRow(group.key, body),
    onSuccess: (res) => {
      qc.setQueryData<GroupPayload>(settingsQueryKey(group.key), (old) => {
        if (!old || old.kind !== "collection") return old;
        return { ...old, rows: [...old.rows, res.row as Row] };
      });
      resetDraft();
    },
    onError: (e) => setError(String(e)),
  });

  const del = useMutation({
    mutationFn: (id: string) => deleteRow(group.key, id),
    onSuccess: (_r, id) => {
      qc.setQueryData<GroupPayload>(settingsQueryKey(group.key), (old) => {
        if (!old || old.kind !== "collection") return old;
        return { ...old, rows: old.rows.filter((r) => r.id !== id) };
      });
    },
  });

  function submitCreate() {
    if (flow === "form-first" && !validateRequired()) return;
    const fieldsToSend = flow === "form-first" ? draftFields : {};
    create.mutate(
      group.id_type === "slug"
        ? { id: slugDraft, fields: fieldsToSend }
        : { fields: fieldsToSend },
    );
  }

  function renderFieldInput(f: Field) {
    const v = draftFields[f.key];
    const set = (nv: unknown) =>
      setDraftFields((s) => ({ ...s, [f.key]: nv }));
    const err = fieldErrors[f.key];
    const wrap = (node: React.ReactNode) => (
      <div key={f.key}>
        {node}
        {err && (
          <div className="mt-1 text-xs text-destructive">{err}</div>
        )}
      </div>
    );
    if (f.type === "string")
      return wrap(<StringField field={f} value={String(v ?? "")} onChange={set} />);
    if (f.type === "int")
      return wrap(<IntField field={f} value={Number(v ?? 0)} onChange={set} />);
    if (f.type === "bool")
      return wrap(<BoolField field={f} value={Boolean(v)} onChange={set} />);
    return null;
  }

  return (
    <section className="rounded-lg border border-border bg-card shadow-sm">
      <header className="flex items-start justify-between gap-4 border-b border-border px-6 py-4">
        <div>
          <h2 className="text-base font-semibold text-card-foreground">
            {group.title}
            <span className="ml-2 text-xs font-normal text-muted-foreground">
              {payload.rows.length} {payload.rows.length === 1 ? "row" : "rows"}
            </span>
          </h2>
          {group.description && (
            <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
              {group.description}
            </p>
          )}
        </div>
        {!showNewForm && (
          <button
            type="button"
            className="shrink-0 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary/90"
            onClick={() => {
              if (flow === "immediate") create.mutate({ fields: {} });
              else setShowNewForm(true);
            }}
          >
            + Add row
          </button>
        )}
      </header>

      {payload.rows.length === 0 && !showNewForm && (
        <div className="border-b border-border px-6 py-8 text-center text-sm text-muted-foreground">
          No rows yet.
        </div>
      )}

      {payload.rows.length > 0 && (
        <RowTable
          group={group}
          rows={payload.rows}
          onDelete={(id) => del.mutate(id)}
        />
      )}

      {showNewForm && flow === "form-first" && (
        <div className="space-y-4 border-t border-border bg-muted/50 px-6 py-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            New row
          </div>
          {group.id_type === "slug" && (
            <div className="grid grid-cols-1 gap-x-6 gap-y-1 md:grid-cols-[minmax(160px,240px)_1fr]">
              <label className="text-sm font-medium text-foreground md:pt-2">
                ID (slug)<span className="ml-1 text-destructive">*</span>
              </label>
              <input
                type="text"
                className="w-full max-w-md rounded-md border border-border bg-card px-3 py-1.5 font-mono text-sm text-foreground shadow-sm focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
                placeholder="e.g. my-tenant"
                value={slugDraft}
                onChange={(e) => setSlugDraft(e.target.value)}
              />
            </div>
          )}
          <div className="space-y-4">
            {(group.fields ?? []).map((f) => renderFieldInput(f))}
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              className="rounded-md border border-border bg-card px-3 py-1.5 text-sm font-medium text-foreground shadow-sm hover:bg-muted"
              onClick={resetDraft}
            >
              Cancel
            </button>
            <button
              type="button"
              className="rounded-md bg-primary px-3.5 py-1.5 text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary/90 disabled:opacity-50"
              onClick={submitCreate}
              disabled={create.isPending}
            >
              {create.isPending ? "Saving…" : "Save row"}
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="border-t border-destructive-soft bg-destructive-soft px-6 py-2 text-sm text-destructive-soft-foreground">
          {error}
        </div>
      )}
    </section>
  );
}

/** RowTable renders each collection row as a table column layout driven by
 *  the manifest's non-secret fields. Secret fields never surface here — they
 *  exist for create/update only and the server responds with preview metadata
 *  that RowTable renders distinctly. */
function RowTable({
  group,
  rows,
  onDelete,
}: {
  group: Group;
  rows: Row[];
  onDelete: (id: string) => void;
}) {
  // Pick the fields worth showing. Secrets aren't displayed as column values
  // by default (the row list is a summary, not credential viewer). If a row
  // happens to carry a non-empty preview, we surface it.
  const displayFields = (group.fields ?? []).filter((f) => !f.secret);

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
            <th className="px-6 py-2 text-left font-medium">ID</th>
            {displayFields.map((f) => (
              <th key={f.key} className="px-3 py-2 text-left font-medium">
                {f.title ?? f.key}
              </th>
            ))}
            <th className="px-6 py-2 text-right font-medium">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((r) => (
            <tr key={r.id} className="hover:bg-muted/50">
              <td className="px-6 py-3">
                <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
                  {r.id}
                </code>
              </td>
              {displayFields.map((f) => (
                <td key={f.key} className="px-3 py-3 text-foreground">
                  {renderFieldValue(f, r.fields[f.key])}
                </td>
              ))}
              <td className="px-6 py-3 text-right">
                <button
                  type="button"
                  className="text-xs font-medium text-destructive hover:opacity-80"
                  onClick={() => onDelete(r.id)}
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function renderFieldValue(f: Field, v: FieldValue | undefined): React.ReactNode {
  if (!v) return <span className="text-muted-foreground">—</span>;
  if ("preview" in v) {
    return v.has_value ? (
      <code className="font-mono text-xs text-muted-foreground">{v.preview}</code>
    ) : (
      <span className="text-muted-foreground">(not set)</span>
    );
  }
  const value = v.value;
  if (value === null || value === undefined || value === "") {
    return <span className="text-muted-foreground">—</span>;
  }
  if (f.type === "bool") {
    return value ? (
      <span className="inline-flex items-center rounded-full bg-success-soft px-2 py-0.5 text-xs font-medium text-success-soft-foreground">
        on
      </span>
    ) : (
      <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
        off
      </span>
    );
  }
  if (f.type === "int") {
    const str = String(value) + (f.unit ? ` ${f.unit}` : "");
    return <span className="font-mono text-xs">{str}</span>;
  }
  // string
  if (f.options && f.options.length > 0) {
    return (
      <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground">
        {String(value)}
      </span>
    );
  }
  return <span>{String(value)}</span>;
}
